import { Component } from "omi";
import { Editor } from "@tiptap/core";
import { Mark, Node } from "@tiptap/pm/model";
import { EditorState } from "@tiptap/pm/state";
import { EditorView } from "@tiptap/pm/view";

/**
 * Finds a node with a specific type and id in the document and returns its position and the node itself.
 *
 * @param state - The current editor state
 * @param nodeName - The name of the node type to search for
 * @param id - The id of the node to find
 * @returns An object containing the position and the node if found, or null if not found
 */
export function findNodeWithPosition(
  state: EditorState,
  nodeName: string,
  id: string,
): { pos: number; node: Node } | null {
  let result: { pos: number; node: Node } | null = null;

  state.doc.descendants((node: Node, pos: number) => {
    if (node.type.name === nodeName && node.attrs.id === id) {
      result = { pos, node };
      return false;
    }
    return true;
  });

  return result;
}

/**
 * Retrieves all DOM elements associated with a specific group ID in the editor view.
 *
 * @param view - The EditorView instance to search within
 * @param group - The group ID to match against
 * @param tag - The tag name to match against
 * @returns An array of Element objects that belong to the specified group
 *
 * @description
 * This function uses the DOM API to find all elements with a 'group' attribute
 * matching the provided group. It's useful for operations that need to
 * manipulate or observe all elements of a particular highlight group.
 */
export function getElementsForGroup<T = HTMLElement>(view: EditorView, group: string, tag: string): T[] {
  return Array.from(view.dom.querySelectorAll(`${tag}[group="${group}"]`)) as T[];
}

/**
 * Checks if a node has a highlight mark.
 * @param node - The node to check.
 * @returns True if the node has a highlight mark, false otherwise.
 */
export function nodeHasHighlightMark(node: Node): boolean {
  return nodeHasMark(node, "highlight");
}

/**
 * Checks if a node has a code mark.
 * @param node - The node to check.
 * @returns True if the node has a code mark, false otherwise.
 */
export function nodeHasCodeMark(node: Node): boolean {
  return nodeHasMark(node, "code");
}

/**
 * Checks if a node has a specific mark type.
 * @param node - The node to check.
 * @param markType - The type of mark to look for.
 * @returns True if the node has the specified mark type, false otherwise.
 */
export function nodeHasMark(node: Node, markType: string): boolean {
  return node.marks.some(mark => mark.type.name === markType);
}

/**
 * Retrieves all marks between two positions in the editor.
 * @param editor - The Editor instance.
 * @param from - The starting position.
 * @param to - The ending position.
 * @returns An array of Mark objects between the specified positions.
 * @throws Error if the positions are out of bounds.
 */
export function getMarksBetweenPositions(editor: Editor, from: number, to: number): Mark[] {
  if (from < 0 || to > editor.state.doc.content.size) {
    throw new Error("Position is out of bounds.");
  }

  const marks: readonly Mark[] = editor.state.doc.resolve(from).marks();
  return Array.from(marks);
}

/**
 * Triggers a rerender of a component node.
 * @param view - The EditorView instance.
 * @param nodePos - The position of the node to rerender.
 */
export function triggerRerender<T extends Component>(view: EditorView, nodePos: number) {
  const domNode = view?.nodeDOM(nodePos);
  const node = domNode instanceof HTMLElement ? (domNode as T) : null;
  node?.updateSelf();
}
